import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, CircularProgress, Alert, MenuItem,
  Autocomplete, Chip,
} from '@mui/material';
import { DeleteForever, Warning } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const REASONS = [
  { value: 'damaged', label: 'Damaged / Broken' },
  { value: 'expired', label: 'Expired / Perished' },
  { value: 'stolen', label: 'Stolen / Missing' },
  { value: 'recalled', label: 'Product Recall' },
  { value: 'quality', label: 'Quality Failure' },
  { value: 'other', label: 'Other' },
];

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

export default function DamagePage() {
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movements, setMovements] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [form, setForm] = useState({
    product: null,
    quantity: '',
    reason: 'damaged',
    description: '',
    branch_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/products?limit=500').then(r => setProducts(r.data.data || []));
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get('/inventory/movements', { params: { type: 'adjustment', limit: 30 } });
      // Filter to only damage/write-off movements (negative quantity)
      setMovements((data.data || []).filter(m => m.quantity_change < 0 && m.note && m.note.match(/damage|expire|stolen|recall|quality|other/i)));
    } finally { setLoadingHistory(false); }
  };

  const handleSubmit = async () => {
    if (!form.product || !form.quantity || +form.quantity < 1) {
      dispatch(showSnackbar({ message: 'Select product and enter quantity', severity: 'warning' }));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/inventory/damage', {
        product_id: form.product.id,
        quantity: +form.quantity,
        reason: form.reason,
        description: form.description,
        branch_id: form.branch_id || undefined,
      });
      dispatch(showSnackbar({ message: `${form.quantity} units of "${form.product.name}" written off`, severity: 'success' }));
      setForm({ product: null, quantity: '', reason: 'damaged', description: '', branch_id: '' });
      loadHistory();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    } finally { setSubmitting(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <DeleteForever color="error" />
        <Typography variant="h5" fontWeight={700}>Damage & Write-Off / نقصان</Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Recording a damage/write-off will <strong>permanently deduct</strong> stock from inventory and create a stock movement record.
        This action cannot be easily reversed — please verify quantities before submitting.
      </Alert>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
        {/* Form */}
        <Card sx={{ flex: '1 1 360px' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>Record Damage / Write-Off</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                options={products}
                getOptionLabel={p => `${p.name}${p.sku ? ` (${p.sku})` : ''}`}
                value={form.product}
                onChange={(_, v) => setForm(f => ({ ...f, product: v }))}
                renderInput={params => (
                  <TextField {...params} label="Product *" size="small" />
                )}
                renderOption={(props, p) => (
                  <li {...props} key={p.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.sku} · Stock: {p.stock ?? '?'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />

              {form.product && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Current stock: <strong>{form.product.stock ?? 'Unknown'}</strong> units
                </Alert>
              )}

              <TextField
                label="Quantity to Write Off *" type="number" size="small"
                value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                inputProps={{ min: 1, max: form.product?.stock }}
              />

              <TextField
                label="Reason *" select size="small"
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              >
                {REASONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>

              <TextField
                label="Description / Notes" multiline rows={2} size="small"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Dropped during warehouse move, shelf collapse..."
              />

              <Button
                variant="contained" color="error" size="large"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <DeleteForever />}
                onClick={handleSubmit}
                disabled={submitting || !form.product || !form.quantity}
              >
                {submitting ? 'Recording…' : 'Record Write-Off'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Summary stats */}
        <Box sx={{ flex: '0 1 220px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card>
            <CardContent>
              <Warning color="warning" sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Recent Write-offs</Typography>
              <Typography variant="h4" fontWeight={700} color="error.main">{movements.length}</Typography>
              <Typography variant="caption" color="text.secondary">Last 30 adjustment records</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Units Lost</Typography>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {Math.abs(movements.reduce((s, m) => s + +m.quantity_change, 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">From damage/write-offs</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* History */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Write-Off History</Typography>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Reason / Notes</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell align="right">Qty Lost</TableCell>
                  <TableCell>Recorded By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No damage records yet</TableCell></TableRow>
                )}
                {movements.map(m => (
                  <TableRow key={m.id} hover>
                    <TableCell>{new Date(m.created_at).toLocaleDateString('en-PK')}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{m.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">{m.sku}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{m.note}</Typography>
                    </TableCell>
                    <TableCell>{m.branch_name || '—'}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`-${Math.abs(m.quantity_change)}`}
                        size="small" color="error"
                      />
                    </TableCell>
                    <TableCell>{m.user_name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
